import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Camera, MapPin } from 'lucide-react';
import { api, fileUrl } from '../lib/api';

const ALBUM_URL = '/gallery?album=ward8';

/**
 * Gentle rotating strip of published Beautiful Ward 8 photos for the homepage.
 * - Animates transform only; pauses on hover/focus; honours prefers-reduced-motion
 *   (the strip then becomes a normal scrollable row).
 * - Every photo opens the album with that photo in the lightbox.
 * - Renders nothing when the album is empty, so the homepage never shows a hole.
 */
export const Ward8PhotoStrip = () => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get('/gallery', { params: { category: 'ward8' } })
      .then((r) => setItems((r.data || []).filter((i) => i.image_url)))
      .catch(() => setItems([]));
  }, []);

  if (!items || items.length === 0) return null;

  const loop = items.length >= 4;
  // Seamless loop: render the row twice and slide by exactly half the track.
  const row = loop ? [...items, ...items] : items;
  const duration = Math.max(30, items.length * 9);

  return (
    <section className="bg-[#0b0f17] border-b border-slate-800 py-10 md:py-12 overflow-hidden" aria-label="Beautiful Ward 8 photos" data-testid="home-ward8-photo-strip">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-[#C9A84C] inline-flex items-center gap-1.5"><Camera className="h-3.5 w-3.5" /> Beautiful Ward 8</p>
          <h2 className="mt-1 text-2xl sm:text-3xl font-black text-white tracking-tight leading-tight" data-testid="home-ward8-photo-strip-headline">Our lakes, farms and villages</h2>
        </div>
        <Link to={ALBUM_URL} className="inline-flex items-center gap-1.5 min-h-11 text-sm font-bold text-white underline decoration-[#C9A84C] decoration-2 underline-offset-4 hover:text-[#C9A84C]" data-testid="home-ward8-photo-strip-album-link">
          Open the album <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      <div className={`ward8-strip ${loop ? 'ward8-strip-loop' : ''}`} data-testid="home-ward8-photo-strip-viewport">
        <div className={`ward8-strip-track ${loop ? '' : 'max-w-6xl mx-auto px-4 sm:px-6 lg:px-8'}`} style={loop ? { '--ward8-strip-duration': `${duration}s` } : undefined}>
          {row.map((item, i) => (
            <Link
              key={`${item.id}-${i}`}
              to={`${ALBUM_URL}&photo=${encodeURIComponent(item.id)}`}
              aria-hidden={loop && i >= items.length ? 'true' : undefined}
              tabIndex={loop && i >= items.length ? -1 : 0}
              className="ward8-strip-card group relative block w-[240px] sm:w-[300px] h-[170px] sm:h-[210px] rounded-xl overflow-hidden shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]"
              data-testid={i < items.length ? `home-ward8-photo-${item.id}` : undefined}
            >
              <img src={fileUrl(item.image_url)} alt={item.title || 'Ward 8 photo'} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent p-3 pt-8">
                <p className="text-white font-serif font-bold text-sm leading-snug line-clamp-1">{item.title}</p>
                {(item.communities || []).length > 0 && (
                  <p className="text-[11px] font-bold text-[#C9A84C] inline-flex items-center gap-1 mt-0.5"><MapPin className="h-3 w-3" /> {item.communities.slice(0, 2).join(' · ')}</p>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Ward8PhotoStrip;
